import { useMemo, useState } from 'react';
import { Select, type SelectProps } from '@mantine/core';
import { useProduct, useProductSearch } from '../api';
import type { ProductRow } from '../types';

function productLabel(p: ProductRow): string {
  return `${p.sku} — ${p.name}`;
}

interface ProductSelectProps extends Omit<SelectProps, 'data' | 'value' | 'onChange' | 'searchable' | 'searchValue' | 'onSearchChange'> {
  value: string;
  /** Reports both the id and a human label so callers can keep a denormalized
   * label alongside the id for the ledger-effect preview without a second fetch. */
  onChange: (productId: string, label: string) => void;
}

/** Searchable product picker (BUILD_SPEC.md §8.6 line entry: "product"). Fetches a
 * page of fuzzy-matched products for the current search text, plus (if a value is
 * already selected but not in that page — e.g. re-opening a saved draft) the single
 * selected product by id, so the label never regresses to a bare ULID. */
export function ProductSelect({ value, onChange, ...rest }: ProductSelectProps) {
  const [search, setSearch] = useState('');
  const { data, isFetching } = useProductSearch(search);
  const { data: selected } = useProduct(value || undefined);

  const options = useMemo(() => {
    const byId = new Map<string, ProductRow>();
    for (const p of data?.items ?? []) byId.set(p.id, p);
    if (selected && !byId.has(selected.id)) byId.set(selected.id, selected);
    return Array.from(byId.values()).map((p) => ({ value: p.id, label: productLabel(p) }));
  }, [data, selected]);

  return (
    <Select
      {...rest}
      placeholder={rest.placeholder ?? 'Search products…'}
      searchable
      searchValue={search}
      onSearchChange={setSearch}
      data={options}
      value={value || null}
      onChange={(newValue) => {
        const match = newValue ? options.find((o) => o.value === newValue) : undefined;
        onChange(newValue ?? '', match?.label ?? '');
      }}
      nothingFoundMessage={isFetching ? 'Searching…' : 'No products found'}
      comboboxProps={{ withinPortal: true }}
    />
  );
}
